/**
 * ToolPageShell — shared per-tool workspace layout (X.3.1 → X.6.4B design
 * refresh).
 *
 * Header + an optional full-width "How this works" panel + a two-column body
 * (input ~1fr / output ~1.1fr; single column below md). Each column is a paper
 * card with an eyebrow label. The output card shows an empty state (dove +
 * prompt) when no content is passed. `spice` is generic — it only tints the
 * header chip; any tool can pass it. Keeps the `inputPlaceholder`/
 * `outputContent` props so the Intake page rides the new look unchanged.
 * x.6.1 tokens throughout; zero raw palette hexes.
 */

import { type ReactNode } from 'react';
import { Sparkles } from 'lucide-react';
import { HubBrandMark } from '@/components/hub/HubBrandMark';
import { ToolPageHeader } from './ToolPageHeader';
import type { AiToolMeta } from '@/pages/AiTools';

export interface ToolPageShellProps {
  tool: AiToolMeta;
  tenantSlug: string;
  /** SPICE mode (X.6.4B): tints the header chip. */
  spice?: boolean;
  /** Full-width panel between the header and the columns (Email/Assessment). */
  howThisWorks?: ReactNode;
  inputPlaceholder: ReactNode;
  /** When undefined, the output card renders the dove empty state. */
  outputContent?: ReactNode;
  /** Eyebrow over the input card; defaults to 'Your notes'. */
  inputTitle?: string;
  /** Eyebrow over the output card; defaults to 'Draft'. */
  outputTitle?: string;
  /**
   * Full-width content rendered below the two columns, inside the page
   * container. Optional; other tool pages pass nothing and render unchanged.
   */
  belowColumns?: ReactNode;
  /**
   * Rendered in the LEFT (input) column below the input card (founder
   * ruling 2026-07-06): companion panels the CM works with WHILE the
   * output is on screen — e.g. the Assessment Drafter recommendations
   * library, whose click-to-insert flow breaks if a long generated
   * report pushes it below the fold.
   */
  belowInput?: ReactNode;
}

/** Output empty state: dove + a calm prompt. */
function EmptyOutput(): ReactNode {
  return (
    <div
      data-testid="tool-output-empty"
      className="flex flex-1 flex-col items-center justify-center px-5 py-8 text-center"
    >
      <HubBrandMark
        label=""
        className="mb-3.5 flex w-10 [&>svg]:h-auto [&>svg]:w-full"
        style={{ color: 'var(--color-sage-300)' }}
      />
      <p className="max-w-[270px] text-sm font-medium leading-relaxed text-sage-400">
        Fill in the notes and generate.
      </p>
    </div>
  );
}

/** Output "drafting" state (X.6.4B): shown by pages while generation runs. */
export function ToolDraftingState({ spice = false }: { spice?: boolean }): ReactNode {
  return (
    <div
      data-testid="tool-output-drafting"
      className="flex flex-1 items-center justify-center gap-2.5 px-5 py-8 text-sage-400"
    >
      <Sparkles size={18} aria-hidden="true" className={spice ? 'text-pepper' : 'text-sage-800'} />
      <span className="text-sm font-medium">{`Drafting in the ${spice ? 'SPICE' : 'Peace'} voice…`}</span>
    </div>
  );
}

const CARD_CLS = 'rounded-card bg-paper p-6 shadow-sh2';
const EYEBROW_CLS = 'text-[11.5px] font-semibold uppercase tracking-[0.16em]';

export function ToolPageShell({
  tool,
  tenantSlug,
  spice = false,
  howThisWorks,
  inputPlaceholder,
  outputContent,
  inputTitle,
  outputTitle,
  belowColumns,
  belowInput,
}: ToolPageShellProps): ReactNode {
  // The two-column workspace (single column below md). The v2 "vertical"
  // stack retired 2026-07-06 (founder UX-core ruling): side-by-side input
  // and output is the tool grammar; nothing hides below the fold.
  const gridCls =
    'mt-[18px] grid grid-cols-1 items-start gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]';
  return (
    <main
      className="mx-auto max-w-[1160px] px-5 pt-8 pb-20 md:px-10"
      data-testid={`tool-shell-${tool.slug}`}
    >
      <ToolPageHeader tool={tool} tenantSlug={tenantSlug} spice={spice} />

      {howThisWorks && <div className="mt-5">{howThisWorks}</div>}

      <div className={gridCls} data-testid="tool-shell-columns">
        <div className="flex min-w-0 flex-col gap-6">
          <section className={CARD_CLS}>
            <span className={`${EYEBROW_CLS} text-sage-400`}>{inputTitle ?? 'Your notes'}</span>
            <div className="mt-4">{inputPlaceholder}</div>
          </section>
          {belowInput}
        </div>
        <section className={`${CARD_CLS} flex min-h-[360px] flex-col`}>
          <span className={`${EYEBROW_CLS} text-warm-400`}>{outputTitle ?? 'Draft'}</span>
          <div className="mt-4 flex flex-1 flex-col">{outputContent ?? <EmptyOutput />}</div>
        </section>
      </div>

      {belowColumns && <div className="mt-6">{belowColumns}</div>}
    </main>
  );
}
